// Compact "Upcoming travel" block for the record drawer Overview: the next
// three eligible trips for one project plus a project-filtered Travel link.
import { useEffect, useState } from "react";
import { T } from "../../../app/tokens.js";
import { InternalLink } from "../../../components/InternalLink.jsx";
import { getUpcomingTrips } from "../../../data/projectTravelClient.js";
import { tx } from "./travelStyles.js";
import { browserZone, tripDateRange, purposeStyle, travelerSummary } from "./travelModel.js";

export function travelProjectHref(projectId) {
  return `/projects/travel?travelProject=${encodeURIComponent(projectId)}`;
}

export function TravelUpcoming({ projectId }) {
  const [state, setState] = useState({ status: "loading", trips: [], error: null });
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!projectId) return undefined;
    let active = true;
    setState({ status: "loading", trips: [], error: null });
    Promise.resolve().then(() => getUpcomingTrips(projectId))
      .then((trips) => { if (active) setState({ status: "ok", trips: trips.slice(0, 3), error: null }); })
      .catch((error) => { if (active) setState({ status: "error", trips: [], error }); });
    return () => { active = false; };
  }, [projectId, retry]);
  const local = browserZone();
  const zone = local || "UTC";
  return (
    <section data-testid="record-upcoming-travel" aria-label="Upcoming travel" style={{ ...tx.card, padding: "12px 18px", minWidth: 0, marginBottom: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
        <span style={{ ...tx.eyebrow, color: T.projectsHubNavy, fontWeight: T.fwHubTitle }}>Upcoming travel</span>
        <span style={{ flex: 1 }} />
        <InternalLink href={travelProjectHref(projectId)} data-testid="record-upcoming-travel-link" style={{ fontSize: T.fsXs, fontWeight: T.fwBold, color: T.blue, textDecoration: "none" }}>Open in Travel →</InternalLink>
      </div>
      {state.status === "loading" && <div aria-busy="true" data-testid="record-upcoming-travel-loading">{[0, 1].map((i) => <div key={i} style={{ height: 12, width: `${70 - i * 20}%`, background: T.lightGray, borderRadius: 4, margin: "6px 0" }} />)}</div>}
      {state.status === "error" && (
        <div role="alert" data-testid="record-upcoming-travel-error" style={{ fontSize: T.fsSm, color: T.mid }}>
          Upcoming travel unavailable{state.error?.message ? `: ${state.error.message}` : ""}. <button type="button" style={tx.link} onClick={() => setRetry((n) => n + 1)}>Retry</button>
        </div>
      )}
      {state.status === "ok" && state.trips.length === 0 && <div data-testid="record-upcoming-travel-empty" style={{ fontSize: T.fsSm, color: T.mid }}>No upcoming trips for this project.</div>}
      {state.status === "ok" && state.trips.length > 0 && !local && <div data-testid="record-upcoming-travel-utc" style={{ fontSize: T.fsXs, color: T.mid }}>Browser time zone unavailable; timed trips shown in UTC.</div>}
      {state.status === "ok" && state.trips.length > 0 && (
        <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
          {state.trips.map((trip) => (
            <li key={trip.id} data-testid={`record-upcoming-trip-${trip.id}`} style={{ display: "flex", gap: 8, alignItems: "baseline", padding: "4px 0", fontSize: T.fsSm, borderTop: `1px solid ${T.travelHair}` }}>
              <span aria-hidden="true" style={{ width: 8, height: 8, borderRadius: 2, background: purposeStyle(trip.purposeType).fg, flexShrink: 0 }} />
              <span style={{ color: T.mid, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>{tripDateRange(trip, zone)}</span>
              <span style={{ fontWeight: T.fwBold, color: T.dark, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{trip.title}</span>
              <span style={{ color: T.mid, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>· {trip.destinationLabel} · {travelerSummary(trip, Array.isArray(trip.travelers))}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
